import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { SelectOption } from 'components/admin/form/selectField';
import { orgSelector } from 'src/redux/selectors';
import { useGetProgramsByOrgId } from 'src/hooks/useProgram';
import { PROGRAM_LIMIT_DEFAULT } from 'src/utils/constants';
import { ProgramType, UserProgramType } from 'src/types/organization.type';

export type ProgramOption = SelectOption & {
   userPrograms: UserProgramType[];
   index?: number;
   created?: Date | string;
   programSlug?: string;
};

/**
 * The site-wide "General Fund" is a single program row with no owning
 * organization, which the API unions into every org's program list. That
 * org-less-ness is the only reliable way to spot it — the name is matched as a
 * literal string in a few older places, and renaming the row would break those.
 */
const isGeneralFund = (program: ProgramType) => !program.organizationId || program.slug === 'general-fund';

/**
 * Deterministic identity order for programs that are otherwise indistinguishable.
 * Mirrors the API's tie-break (index, then created, then id) so both ends mean
 * the same row when nothing else separates them.
 */
const byIdentity = (a: ProgramType, b: ProgramType) =>
   (a.index ?? Number.MAX_SAFE_INTEGER) - (b.index ?? Number.MAX_SAFE_INTEGER) ||
   String(a.created ?? '').localeCompare(String(b.created ?? '')) ||
   String(a.id).localeCompare(String(b.id));

/**
 * Collapse programs that share a slug.
 *
 * Slugs are not unique. The site-wide programs -- General Fund above all --
 * carry no owning organization, and MySQL exempts NULLs from the
 * UNIQUE (organizationId, index) index, so an environment can hold several
 * rows with the same slug and name. Every one of them lands in this list, and
 * the picker then shows two options a person cannot tell apart while every
 * `.find()` over the list silently takes whichever came back first -- the
 * `?programSlug=` match, the General Fund default, and the option a booking is
 * ultimately attributed to.
 *
 * Programs with no slug are never collapsed: there is nothing to match on.
 */
export const dedupeBySlug = (programs: ProgramType[]): ProgramType[] => {
   const winners = new Set<string>();
   const claimed = new Set<string>();
   for (const program of [...programs].sort(byIdentity)) {
      const key = program.slug;
      if (!key) {
         winners.add(program.id);
         continue;
      }
      if (claimed.has(key)) continue;
      claimed.add(key);
      winners.add(program.id);
   }
   // Filter rather than return the sorted array, so the API's ordering survives.
   return programs.filter(program => winners.has(program.id));
};

/**
 * Single source of truth for the proceeds program picker's options.
 *
 * Fetches the current org's active programs and, when the org has opted into
 * `configuration.hideGeneralFund`, drops the site-wide General Fund from the
 * list. Orgs that would be left with nothing keep the unfiltered list, so a
 * booking can always resolve a programId.
 */
export const useOrgPrograms = () => {
   const { organization } = useSelector(orgSelector);
   const { data: getProgramsRes } = useGetProgramsByOrgId({
      organizationId: organization?.id,
      limit: PROGRAM_LIMIT_DEFAULT,
   });

   const programs: ProgramType[] = useMemo(() => {
      const active = dedupeBySlug((getProgramsRes?.data?.entities ?? []).filter((p: ProgramType) => p.isActive));
      if (!organization?.configuration?.hideGeneralFund) return active;

      const own = active.filter((p: ProgramType) => !isGeneralFund(p));
      return own.length ? own : active;
   }, [getProgramsRes?.data?.entities, organization?.configuration?.hideGeneralFund]);

   const options: ProgramOption[] = useMemo(
      () =>
         programs.map((p: ProgramType) => ({
            label: p.name,
            value: p.id,
            userPrograms: p.users,
            index: p.index,
            created: p.created,
            programSlug: p.slug,
         })),
      [programs],
   );

   return { programs, options };
};

export default useOrgPrograms;
